import React, { useState, useEffect } from 'react';
import { clientsAPI, servicePillarsAPI } from '../../utils/api';
import { formatApiError } from '../../utils/errorFormatter';
import { X, Loader2, Plus, Trash2 } from 'lucide-react';

const ORGANIZATION_TYPES = ['GLC', 'Statutory Body', 'Private Sector', 'Government Agency', 'NGO', 'Other'];
const PRIORITIES = ['High', 'Medium', 'Low'];
const PROSPECTING_STATUSES = ['Identified', 'Researched', 'Contacted', 'In Discussion', 'Proposal Sent', 'Won', 'Lost', 'On Hold'];
const INDUSTRIES = ['Government', 'GLC', 'Private', 'NGO', 'Education', 'Corporate'];

const ClientModal = ({ isOpen, onClose, client = null, defaultCategory = 'prospect', onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [servicePillars, setServicePillars] = useState([]);

  const [formData, setFormData] = useState({
    client_name: '',
    industry: 'Government',
    sector: '',
    company_size: '',
    address: '',
    website: '',
    notes: '',
    organization_type: 'Private Sector',
    parent_entity: '',
    priority: 'Medium',
    prospecting_status: 'Identified',
    relevant_services: [],
  });

  const [contacts, setContacts] = useState([{
    full_name: '',
    designation: '',
    email: '',
    phone: ''
  }]);

  useEffect(() => {
    if (isOpen) {
      fetchServicePillars();
      if (client) {
        setFormData({
          client_name: client.client_name || '',
          industry: client.industry || 'Government',
          sector: client.sector || '',
          company_size: client.company_size || '',
          address: client.address || '',
          website: client.website || '',
          notes: client.notes || '',
          organization_type: client.organization_type || 'Private Sector',
          parent_entity: client.parent_entity || '',
          priority: client.priority || 'Medium',
          prospecting_status: client.prospecting_status || 'Identified',
          relevant_services: client.relevant_services || [],
        });
      }
    }
  }, [isOpen, client]);

  const fetchServicePillars = async () => {
    try {
      const res = await servicePillarsAPI.getAll();
      setServicePillars(res.data);
    } catch (err) {
      console.error('Error fetching service pillars:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleService = (pillarName) => {
    setFormData(prev => ({
      ...prev,
      relevant_services: prev.relevant_services.includes(pillarName)
        ? prev.relevant_services.filter(s => s !== pillarName)
        : [...prev.relevant_services, pillarName]
    }));
  };

  const handleContactChange = (index, field, value) => {
    const updated = [...contacts];
    updated[index][field] = value;
    setContacts(updated);
  };

  const addContact = () => {
    setContacts([...contacts, { full_name: '', designation: '', email: '', phone: '' }]);
  };

  const removeContact = (index) => {
    setContacts(contacts.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.client_name) throw new Error('Client name is required');

      if (client) {
        await clientsAPI.update(client._id, formData);
      } else {
        await clientsAPI.create({ ...formData, category: defaultCategory });
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(formatApiError(err, 'Failed to save client'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const priorityColor = {
    High: 'bg-red-100 text-red-700 border-red-200',
    Medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    Low: 'bg-green-100 text-green-700 border-green-200',
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="client-modal-overlay"
    >
      <div
        className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="client-modal"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-slate-900" data-testid="client-modal-title">
            {client ? 'Edit Client' : defaultCategory === 'prospect' ? 'New Prospect' : 'New Client'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"
            data-testid="client-modal-close"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="client-modal-error">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Organization Name *</label>
                <input
                  type="text"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  data-testid="client-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Organization Type</label>
                <select
                  name="organization_type"
                  value={formData.organization_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="org-type-select"
                >
                  {ORGANIZATION_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Industry</label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="industry-select"
                >
                  {INDUSTRIES.map(i => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Sector</label>
                <input
                  type="text"
                  name="sector"
                  value={formData.sector}
                  onChange={handleChange}
                  placeholder="e.g. Energy & Utilities"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="sector-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Parent Entity</label>
                <input
                  type="text"
                  name="parent_entity"
                  value={formData.parent_entity}
                  onChange={handleChange}
                  placeholder="e.g. Khazanah Nasional"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="parent-entity-input"
                />
              </div>
            </div>
          </div>

          {/* Prospecting Info */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Prospecting</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                <div className="flex gap-2" data-testid="priority-selector">
                  {PRIORITIES.map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, priority: p }))}
                      className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                        formData.priority === p ? priorityColor[p] : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Pipeline Status</label>
                <select
                  name="prospecting_status"
                  value={formData.prospecting_status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="pipeline-status-select"
                >
                  {PROSPECTING_STATUSES.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Company Size</label>
                <select
                  name="company_size"
                  value={formData.company_size}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="company-size-select"
                >
                  <option value="">Select Size</option>
                  <option value="Small (1-50)">Small (1-50)</option>
                  <option value="Medium (51-250)">Medium (51-250)</option>
                  <option value="Large (251-1000)">Large (251-1000)</option>
                  <option value="Enterprise (1000+)">Enterprise (1000+)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Service Lines */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Relevant Service Lines</h3>
            <div className="flex flex-wrap gap-2" data-testid="service-lines-selector">
              {servicePillars.map(pillar => (
                <button
                  key={pillar._id}
                  type="button"
                  onClick={() => toggleService(pillar.pillar_name)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    formData.relevant_services.includes(pillar.pillar_name)
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {pillar.pillar_name}
                </button>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Additional Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="website-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="address-input"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="notes-textarea"
                />
              </div>
            </div>
          </div>

          {/* Contacts (only for new clients) */}
          {!client && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Contacts (Optional)</h3>
                <button
                  type="button"
                  onClick={addContact}
                  className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                  data-testid="add-contact-btn"
                >
                  <Plus size={16} /> Add Contact
                </button>
              </div>
              <div className="space-y-3">
                {contacts.map((contact, index) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="Full Name"
                          value={contact.full_name}
                          onChange={(e) => handleContactChange(index, 'full_name', e.target.value)}
                          className="px-3 py-1.5 border border-slate-200 rounded text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Designation"
                          value={contact.designation}
                          onChange={(e) => handleContactChange(index, 'designation', e.target.value)}
                          className="px-3 py-1.5 border border-slate-200 rounded text-sm"
                        />
                        <input
                          type="email"
                          placeholder="Email"
                          value={contact.email}
                          onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                          className="px-3 py-1.5 border border-slate-200 rounded text-sm"
                        />
                        <input
                          type="tel"
                          placeholder="Phone"
                          value={contact.phone}
                          onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                          className="px-3 py-1.5 border border-slate-200 rounded text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeContact(index)}
                        className="text-red-500 hover:text-red-700 mt-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
              data-testid="client-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="client-modal-submit"
            >
              {loading && <Loader2 className="animate-spin" size={16} />}
              {client ? 'Update Client' : defaultCategory === 'prospect' ? 'Create Prospect' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientModal;
